/**
 * An agent edit is a change in history with the session's name on it
 * (RFC 0301 §3e, S7 and S8).
 *
 * HISTORY IS THE ONLY RECORD. An agent write stamps its Automerge change
 * message with the session; this file reads those changes back, says what each
 * one did to the text, finds that text in the document as it is now, and undoes
 * it as a new change written by the person. Nothing is stored beside the
 * document, so there is nothing to keep in step with it.
 *
 * UNDO NEVER REVERTS A PERSON'S WORDS. An edit is found by the text it added
 * together with a little of the text around it. If the person has since typed
 * inside that span, or around it closely enough that the edit cannot be found
 * exactly once, undo refuses rather than guess.
 */

import type { Repo } from '@automerge/automerge-repo/slim'
import { Automerge as A } from '@automerge/automerge-repo/slim'
import { readDocumentContent } from '../documents/reads'
import { undoChange } from '../documents/undo'
/**
 * The message on an Automerge change, whoever made it (RFC 0313 3a).
 *
 * A session stamps `sessionId`; a surface writing for a person stamps
 * `authorId`. A change with neither is from before this existed, or from a
 * surface not yet taught to stamp one, and history still shows it, unattributed.
 */
export interface ChangeNote {
  /** The session, when an agent wrote it. */
  sessionId?: string
  /** The prompt or comment the agent's write answered (RFC 0313 3a). */
  turnId?: string
  /** The person, by `userDocId`, when a surface wrote it for them. */
  authorId?: string
  /** One line on why, shown with the diff. */
  note?: string
  /** Set only on an undo: the hash of the change it reverses. */
  reverts?: string
  /** Set only on a restore: the heads the content was taken from. */
  restores?: string[]
  /** Set only on a merge: the branch it came from. */
  merges?: string
}

/** How much text either side of an edit identifies it. */
const CONTEXT = 32

export function changeNote(note: ChangeNote): string {
  return JSON.stringify(note)
}

/** The parsed note, or null for a change nobody stamped. */
export function parseChangeMessage(
  message: string | null | undefined,
): ChangeNote | null {
  if (!message || message[0] !== '{') return null
  try {
    const parsed = JSON.parse(message) as Record<string, unknown>
    const out: ChangeNote = {}
    if (typeof parsed.sessionId === 'string') out.sessionId = parsed.sessionId
    if (typeof parsed.turnId === 'string') out.turnId = parsed.turnId
    if (typeof parsed.authorId === 'string') out.authorId = parsed.authorId
    if (typeof parsed.note === 'string') out.note = parsed.note
    if (typeof parsed.reverts === 'string') out.reverts = parsed.reverts
    if (
      Array.isArray(parsed.restores) &&
      parsed.restores.every((h) => typeof h === 'string')
    ) {
      out.restores = parsed.restores as string[]
    }
    if (typeof parsed.merges === 'string') out.merges = parsed.merges
    return Object.keys(out).length ? out : null
  } catch {
    return null
  }
}

/** One agent edit, as history tells it. */
export interface AgentEdit {
  hash: string
  sessionId: string
  note?: string
  /** Seconds since the epoch, as Automerge records a change's time. */
  time: number
  /** The text the edit took out, and the text it put in. */
  removed: string
  added: string
  /** Text immediately before and after the edit, when it was made. */
  contextBefore: string
  contextAfter: string
  /** An undo naming this change exists. */
  reverted: boolean
}

/** Where an edit is in the document now. `from === to` for a pure deletion. */
export interface EditLocation {
  from: number
  to: number
}

/** The single region `after` differs from `before` in. */
export function region(before: string, after: string) {
  let head = 0
  const max = Math.min(before.length, after.length)
  while (head < max && before[head] === after[head]) head++
  let tail = 0
  while (
    tail < before.length - head &&
    tail < after.length - head &&
    before[before.length - 1 - tail] === after[after.length - 1 - tail]
  ) {
    tail++
  }
  return {
    head,
    removed: before.slice(head, before.length - tail),
    added: after.slice(head, after.length - tail),
    contextBefore: before.slice(Math.max(0, head - CONTEXT), head),
    contextAfter: after.slice(
      after.length - tail,
      Math.min(after.length, after.length - tail + CONTEXT),
    ),
  }
}

/**
 * Every agent edit to a document's text, oldest first.
 *
 * One change at a time: the text before is the document at the change's
 * dependencies, the text after is the document at the change. A change that
 * did not touch the text (a field, a timestamp) is not an edit.
 */
export function projectAgentEdits(doc: unknown): AgentEdit[] {
  if (!doc || typeof doc !== 'object') return []
  let changes: A.DecodedChange[]
  try {
    changes = A.getAllChanges(doc as A.Doc<unknown>).map((c) =>
      A.decodeChange(c),
    )
  } catch {
    return []
  }
  const reverted = new Set<string>()
  for (const c of changes) {
    const m = parseChangeMessage(c.message)
    if (m?.reverts) reverted.add(m.reverts)
  }
  const out: AgentEdit[] = []
  for (const c of changes) {
    const m = parseChangeMessage(c.message)
    if (!m?.sessionId || !c.hash) continue
    let before: string
    let after: string
    try {
      before = readDocumentContent(A.view(doc as A.Doc<unknown>, c.deps))
      after = readDocumentContent(A.view(doc as A.Doc<unknown>, [c.hash]))
    } catch {
      continue
    }
    if (before === after) continue
    const r = region(before, after)
    out.push({
      hash: c.hash,
      sessionId: m.sessionId,
      ...(m.note ? { note: m.note } : {}),
      time: c.time,
      removed: r.removed,
      added: r.added,
      contextBefore: r.contextBefore,
      contextAfter: r.contextAfter,
      reverted: reverted.has(c.hash),
    })
  }
  return out
}

/**
 * Where an edit's text is now, or null when it cannot be found exactly once.
 *
 * The added text with the context either side must appear once. Found twice is
 * as much a refusal as not found: undoing the wrong copy would revert words the
 * agent never wrote.
 */
export function locateAgentEdit(
  current: string,
  edit: Pick<AgentEdit, 'added' | 'contextBefore' | 'contextAfter'>,
): EditLocation | null {
  const needle = edit.contextBefore + edit.added + edit.contextAfter
  if (!needle) return null
  const at = current.indexOf(needle)
  if (at < 0 || current.indexOf(needle, at + 1) >= 0) return null
  const from = at + edit.contextBefore.length
  return { from, to: from + edit.added.length }
}

export type UndoResult =
  | { ok: true }
  | { ok: false; reason: 'not-found' | 'already-undone' | 'changed' }

/**
 * Undo one agent edit. Now a name for `undoChange` (RFC 0313 3d), which takes
 * back any row; an agent edit is a row of one change, so the result is the same.
 */
export async function undoAgentEdit(
  repo: Repo,
  projectId: string,
  documentId: string,
  hash: string,
): Promise<UndoResult> {
  const out = await undoChange(repo, projectId, documentId, hash)
  if (out.ok) return { ok: true }
  return {
    ok: false,
    reason: out.reason === 'unsupported' ? 'changed' : out.reason,
  }
}

/**
 * True when every change between `before` and `after` is an undo (§3g).
 *
 * The app reads this before offering a pause to the paired session: an undo is
 * the person's write, so without this the session would notice the change and
 * make the edit it just lost all over again.
 */
export function isUndoChange(before: unknown, after: unknown): boolean {
  if (!before || !after) return false
  try {
    const changes = A.getChanges(
      before as A.Doc<unknown>,
      after as A.Doc<unknown>,
    )
    return (
      changes.length > 0 &&
      changes.every(
        (c) => !!parseChangeMessage(A.decodeChange(c).message)?.reverts,
      )
    )
  } catch {
    return false
  }
}
